/**
 * Hand markers — the pins a person placed, and the candidates beneath them.
 *
 * WHAT THIS REPLACED, AND WHY (ADR-022 §1c). Until 2026-08-12 this drew each
 * analyzer branch as a thin pink line (`LineBasicMaterial`, `#f299ff`) over
 * hands that are frequently small and pink, and lit the selected branch's
 * vertices. Allen's field report after dozens of grafts: the markers cannot be
 * read. A line says "somewhere over there" about a thing the analyzer chose;
 * the question a person is actually answering is "which digit is THIS one",
 * and the object they are answering about is now their own pin.
 *
 * So the layer draws PINS: a dot where a person pointed, in the digit's own
 * colour, with a RING when the answer is theirs (a locked answer) and no ring
 * when it is only the pre-fill's proposal (D4's condition — a proposal must
 * never look like a person's statement). Candidates nobody has pinned stay, one
 * step quieter: small grey ghosts you can click to adopt. The bright things on
 * screen are the answers; the evidence is underneath them, where §3 puts it.
 *
 * PARENTED TO THE BONE, NOT PLACED IN THE WORLD. Pins are BIND-world (ADR-022
 * §5.1) and the subject is usually playing a clip — markers dropped at bind
 * coordinates would hang in the air where the hand rests while the hand walks
 * away. Each marker is expressed in the hand bone's own space via the inverse
 * bind matrix and added as a child of that bone, so it rides the animation for
 * free. This is the one thing kept unchanged from the line version, because it
 * was the part that was right.
 *
 * MESHES, NOT POINTS. This renderer is WebGPU, where `PointsMaterial` does not
 * rasterise — the cloud existed, the probe counted it, and nothing appeared on
 * screen. Dots are sphere meshes and rings are torus meshes, both drawn with
 * the material family the bone visual already proves under this backend.
 *
 * View-layer only — it draws what it is handed and decides nothing. Which pin
 * is corroborated, which name is a proposal, which candidate is a ghost: all
 * decided in `hands/digit-pins` and handed down as data (ADR-022 §5.2).
 * Depends on: three, adapter-three (skinned mesh lookup).
 * Depended on by: main (wires it to the Hands panel).
 */
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshPhongMaterial,
  Object3D,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { markViewportDebris, soleSkinnedMesh } from '@harryhausen/adapter-three';
import { DIGIT_HUE, UNNAMED_HUE, WRIST_HUE } from './marker-hues.js';

interface P3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** One pin, as the view needs it — a place, a name, and how sure the name is. */
export interface PinMarker {
  readonly id: string;
  /** BIND-world, exactly as stored. */
  readonly tipWorld: P3;
  /** The digit name, or null for a pin nobody has named yet. */
  readonly digit: string | null;
  /**
   * `human` locks (a ring); `proposed` does not (D4 — a pre-fill must be
   * visibly distinct from a person's own answer); `unnamed` is a placed pin
   * still waiting for its name.
   */
  readonly state: 'human' | 'proposed' | 'unnamed';
  /** False when no resolution separated anything here — drawn hollow, never hidden (§9). */
  readonly corroborated: boolean;
  /** The corroborating branch's medial path, when it has one — what the vertex light follows. */
  readonly chainWorld?: readonly P3[];
  /**
   * The digit's pinned first knuckle (ADR-024 §2.2), BIND-world, or absent.
   * Drawn as a smaller ringed dot in the digit's own hue — same identity, one
   * step quieter than the tip — and pickable as `<pinId>:knuckle`.
   */
  readonly knuckleWorld?: P3 | null;
}

/** A consensus candidate nobody pinned — a suggestion, one step quieter (§3.4). */
export interface GhostMarker {
  readonly id: string;
  readonly tipWorld: P3;
}

/** The isolated hand's own vertices, and the voxel edge they were measured at. */
export interface MeasuredRegion {
  readonly positions: Float32Array;
  readonly voxelSize: number;
}

export interface HandScene {
  readonly pins: readonly PinMarker[];
  readonly ghosts: readonly GhostMarker[];
  /**
   * THE WRIST (ADR-025): where the person said the wrist is, BIND-WORLD — or
   * absent. Drawn as its own mark (the joint's hue, an open ring, marker id
   * `wrist`) so it can be re-opened and taken away like any other answer.
   */
  readonly wristWorld?: P3 | null;
  /** The pin or ghost the pointer is over — lit, and its vertices shown. */
  readonly hovered: string | null;
  readonly region: MeasuredRegion;
}

export interface HandMarkers {
  /**
   * Draw ONE HAND'S pins and ghosts, replacing only that hand's markers.
   *
   * PER HAND, and it has to be: the panel renders a card per hand and each one
   * pushes its own view, so a layer with a single shared set leaves whichever
   * hand rendered LAST holding the screen — the right hand's answers vanishing
   * because the left hand re-rendered. Worse, candidate ids (`tip-1`) are minted
   * per hand and collide across them, so a shared registry also aims `tip-1` at
   * whichever hand wrote it last. Both were MEASURED, by an e2e click that
   * landed on the wrong hand and was refused for it.
   */
  show(handBoneName: string, scene: HandScene): void;
  /**
   * THE DOTS ARE THE PICK SURFACE. Allen's field report on the first version:
   * *"my instinct was to click the discs in 3D"* — and he was right, the app was
   * wrong. Clicking the MESH at a fingertip is a knife-edge target: measured
   * through the e2e, a ray passing 3 mm from a tip hits nothing at all, because
   * a fingertip is a couple of millimetres across and the voxel-derived tip can
   * sit a voxel proud of the surface. The dot is a big, unmissable object at
   * exactly the place that matters, so it is what the ray tests first.
   *
   * Each returned object carries `userData.handBone` and `userData.markerId`.
   */
  pickables(): readonly Object3D[];
  /** Remove every marker — called when the panel closes or the subject swaps. */
  clear(): void;
  /** Marker count, for the e2e to see that something was really drawn. */
  readonly count: number;
  /**
   * Where the markers ACTUALLY ended up, in world space, measured off the live
   * objects rather than echoed from the inputs. A marker is a trust surface: it
   * tells a person "this is the place you named", so a transform error here does
   * not degrade the feature, it inverts it. `pinFromHandRatio` — the pins'
   * distance from the hand bone as a fraction of the hand's own extent — is the
   * one number that catches a space mistake: pins on a hand sit ON it, and
   * anything approaching 1 is drawing somewhere else entirely.
   */
  probe(): {
    readonly dots: number;
    readonly ghosts: number;
    readonly litPoints: number;
    readonly handExtent: number;
    readonly pinFromHandRatio: number | null;
  };
  /**
   * Where a pin lands ON SCREEN, so a test can aim a real click at it and the
   * panel can anchor a label. Null when it is not drawn or is behind the
   * camera. (The pose tool's `handleScreenXY` precedent — a gesture surface
   * that cannot be aimed at cannot be tested through the door people use.)
   *
   * Takes the HAND as well as the id, because `tip-1` names a different place
   * on each hand and a caller that omitted it would be aiming at whichever hand
   * happened to draw last.
   */
  screenXY(
    handBoneName: string,
    id: string,
    camera: { projectPoint(p: Vector3): { x: number; y: number } | null },
  ): { x: number; y: number } | null;
}

/** Digit colours — one hue per finger, so the model and the panel rows agree at a glance (tokens: marker-hues.ts). */
const DIGIT_COLOR: Readonly<Record<string, number>> = DIGIT_HUE;
const UNNAMED = UNNAMED_HUE;
const GHOST = 0x8a7fa3;
/** The wrist mark (ADR-025) — the joint's own hue, distinct from every digit. */
const WRIST = WRIST_HUE;
/** The wrist marker's id — one per hand, so it needs no minting. */
export const WRIST_MARKER_ID = 'wrist';
const LIT = new Color(0.95, 0.6, 1);

/** A flat, view-independent dot: the proven material family, lit from nowhere. */
function dotMaterial(color: number, opacity: number): MeshPhongMaterial {
  return new MeshPhongMaterial({
    color: 0x000000,
    // Emissive-only so a pin reads the same from every angle — a marker whose
    // legibility depends on where the light is is a marker that disappears.
    emissive: color,
    depthTest: false,
    transparent: true,
    opacity,
  });
}

/** The scene is asked for at draw time — the subject is replaceable, and a
 *  captured scene would go stale the moment it is (#61's mixer-birth lesson). */
export function createHandMarkers(sceneOf: () => Object3D): HandMarkers {
  /** One hand's drawn objects — replaced wholesale when that hand redraws. */
  interface HandLayer {
    readonly objects: Object3D[];
    readonly clouds: LineSegments[];
    /** id → dot, for `screenXY`. Scoped per hand, since `tip-1` is not unique. */
    readonly dots: Map<string, Mesh>;
    pins: number;
    ghosts: number;
  }
  const layers = new Map<string, HandLayer>();

  function dispose(layer: HandLayer): void {
    for (const object of [...layer.objects, ...layer.clouds]) {
      object.removeFromParent();
      const drawable = object as Mesh;
      drawable.geometry?.dispose();
      (drawable.material as MeshPhongMaterial | LineBasicMaterial | undefined)?.dispose();
    }
  }

  function clear(): void {
    for (const layer of layers.values()) dispose(layer);
    layers.clear();
  }

  return {
    pickables() {
      const out: Object3D[] = [];
      for (const layer of layers.values()) for (const dot of layer.dots.values()) out.push(dot);
      return out;
    },
    get count() {
      let n = 0;
      for (const layer of layers.values()) n += layer.objects.length + layer.clouds.length;
      return n;
    },
    probe() {
      // Measured across every drawn hand, off the LIVE objects. The ratio is
      // computed PER HAND — each dot against its own hand bone — so two hands'
      // markers cannot average into a plausible-looking number.
      let dots = 0;
      let ghosts = 0;
      let litPoints = 0;
      let extent = 0;
      let ratioSum = 0;
      let ratioCount = 0;
      const p = new Vector3();
      for (const layer of layers.values()) {
        dots += layer.pins;
        ghosts += layer.ghosts;
        litPoints += layer.clouds.reduce((t, c) => t + c.geometry.getAttribute('position').count / 6, 0);
        const bone = layer.objects[0]?.parent ?? null;
        if (bone === null) continue;
        bone.updateWorldMatrix(true, false);
        const handOrigin = new Vector3().setFromMatrixPosition(bone.matrixWorld);
        const centroid = new Vector3();
        let counted = 0;
        let handExtent = 0;
        for (const dot of layer.dots.values()) {
          dot.updateWorldMatrix(true, false);
          p.setFromMatrixPosition(dot.matrixWorld);
          centroid.add(p);
          counted += 1;
          handExtent = Math.max(handExtent, p.distanceTo(handOrigin));
        }
        extent = Math.max(extent, handExtent);
        if (counted > 0 && handExtent > 0) {
          ratioSum += centroid.divideScalar(counted).distanceTo(handOrigin) / handExtent;
          ratioCount += 1;
        }
      }
      return {
        dots,
        ghosts,
        litPoints,
        handExtent: extent,
        pinFromHandRatio: ratioCount > 0 ? ratioSum / ratioCount : null,
      };
    },
    screenXY(handBoneName, id, camera) {
      const dot = layers.get(handBoneName)?.dots.get(id);
      if (dot === undefined) return null;
      dot.updateWorldMatrix(true, false);
      return camera.projectPoint(new Vector3().setFromMatrixPosition(dot.matrixWorld));
    },
    clear,
    show(handBoneName, view) {
      const previous = layers.get(handBoneName);
      if (previous !== undefined) dispose(previous);
      const layer: HandLayer = { objects: [], clouds: [], dots: new Map(), pins: 0, ghosts: 0 };
      layers.set(handBoneName, layer);
      const mesh = soleSkinnedMesh(sceneOf());
      const boneIndex = mesh.skeleton.bones.findIndex((b) => b.name === handBoneName);
      if (boneIndex < 0) {
        // The panel offered a hand this scene does not have — a wiring error
        // between rig and scene, and §8 says name it rather than draw nothing.
        throw new Error(
          `hand markers: bone "${handBoneName}" is not in the skeleton — the rig and the scene disagree.`,
        );
      }
      const bone = mesh.skeleton.bones[boneIndex]!;
      // World-bind → bone-local. `boneInverses` IS the inverse bind matrix, the
      // same quantity the kernel calls worldBind, so a clip left applied to the
      // scene cannot skew this.
      const toBoneLocal = mesh.skeleton.boneInverses[boneIndex] as Matrix4;
      const r = view.region.voxelSize;

      const place = (object: Object3D, at: P3): void => {
        const local = new Vector3(at.x, at.y, at.z).applyMatrix4(toBoneLocal);
        object.position.copy(local);
        object.renderOrder = 999;
        object.frustumCulled = false;
        // STAMPED, NOT NAMED: the export and discovery doors recognise debris
        // by this identity alone (viewport-debris). Fourteen unstamped
        // `hand-ghost:*` discs shipped inside the tiger's file on 2026-09-02;
        // a name is for the panel, the stamp is for the doors.
        markViewportDebris(object, 'hand-marker');
        bone.add(object);
        layer.objects.push(object);
      };

      for (const ghost of view.ghosts) {
        // Small, grey, and unmistakably NOT an answer — but present, because a
        // real digit nobody pinned must not be invisible (the nudge's twin).
        // ROLLOVER GROWS IT HARD (Allen, 2026-08-12: "sometimes it was hard to
        // pick out the active target"): the hovered disc is nearly double, and
        // wears a ring, because the thing under the cursor has to be findable
        // without reading anything.
        const lit = view.hovered === ghost.id;
        const dot = new Mesh(new SphereGeometry(r * (lit ? 2.2 : 1.1), 14, 10), dotMaterial(GHOST, lit ? 1 : 0.5));
        dot.name = `hand-ghost:${handBoneName}:${ghost.id}`;
        dot.userData['handBone'] = handBoneName;
        dot.userData['markerId'] = ghost.id;
        place(dot, ghost.tipWorld);
        layer.dots.set(ghost.id, dot);
        layer.ghosts += 1;
        if (lit) {
          const ring = new Mesh(new TorusGeometry(r * 3.1, r * 0.22, 8, 24), dotMaterial(0xffffff, 0.85));
          ring.name = `hand-hover:${handBoneName}:${ghost.id}`;
          place(ring, ghost.tipWorld);
        }
      }

      // ── THE WRIST (ADR-025): the first answer on a hand, on the model ─────
      if (view.wristWorld !== undefined && view.wristWorld !== null) {
        const wLit = view.hovered === WRIST_MARKER_ID;
        const wDot = new Mesh(new SphereGeometry(r * (wLit ? 2.4 : 1.6), 16, 12), dotMaterial(WRIST, 1));
        wDot.name = `hand-wrist:${handBoneName}`;
        wDot.userData['handBone'] = handBoneName;
        wDot.userData['markerId'] = WRIST_MARKER_ID;
        place(wDot, view.wristWorld);
        layer.dots.set(WRIST_MARKER_ID, wDot);
        const wRing = new Mesh(new TorusGeometry(r * 2.6, r * 0.3, 8, 28), dotMaterial(WRIST, 0.85));
        wRing.name = `hand-wrist-ring:${handBoneName}`;
        place(wRing, view.wristWorld);
        if (wLit) {
          const hover = new Mesh(new TorusGeometry(r * 3.5, r * 0.22, 8, 24), dotMaterial(0xffffff, 0.85));
          hover.name = `hand-hover:${handBoneName}:${WRIST_MARKER_ID}`;
          place(hover, view.wristWorld);
        }
      }

      for (const pin of view.pins) {
        const lit = view.hovered === pin.id;
        const color = pin.digit === null ? UNNAMED : DIGIT_COLOR[pin.digit] ?? UNNAMED;
        const dot = new Mesh(
          new SphereGeometry(r * (lit ? 2.6 : 1.8), 16, 12),
          dotMaterial(color, pin.state === 'proposed' ? 0.65 : 1),
        );
        dot.name = `hand-pin:${handBoneName}:${pin.id}`;
        dot.userData['handBone'] = handBoneName;
        dot.userData['markerId'] = pin.id;
        place(dot, pin.tipWorld);
        layer.dots.set(pin.id, dot);
        layer.pins += 1;

        // THE LOCK. A ring around the dot means a PERSON said this — the
        // difference D4 requires between an answer and a pre-fill, carried on
        // the model and not only in the panel, because the model is where the
        // person is looking when they decide the pre-fill is wrong.
        if (pin.state === 'human') {
          const ring = new Mesh(new TorusGeometry(r * 2.6, r * 0.35, 8, 24), dotMaterial(color, 0.95));
          ring.name = `hand-lock:${handBoneName}:${pin.id}`;
          place(ring, pin.tipWorld);
        }
        // A pin the volume never corroborated wears a second, wider ring: it
        // grafts (§9 — the mitten, the fused digit, the sixth finger), and it
        // says on the model that nothing down there was measured.
        // The hover ring, on pins too — same rule, same reason.
        if (lit) {
          const ring = new Mesh(new TorusGeometry(r * 3.6, r * 0.22, 8, 24), dotMaterial(0xffffff, 0.85));
          ring.name = `hand-hover:${handBoneName}:${pin.id}`;
          place(ring, pin.tipWorld);
        }
        if (!pin.corroborated) {
          const halo = new Mesh(new TorusGeometry(r * 3.4, r * 0.18, 8, 24), dotMaterial(color, 0.5));
          halo.name = `hand-unsupported:${handBoneName}:${pin.id}`;
          place(halo, pin.tipWorld);
        }

        // ★ THE MEASURED VERTICES, on the hovered pin only. A dot says "here";
        //   this says "these are the vertices the volume attributed to the thing
        //   you are pointing at". On the android's right hand it is what makes
        //   the cuff rim visible AS a cuff rim — five voxels of geometry at the
        //   wrist — instead of a row reading `branch-4 · persists 5`.
        if (lit && pin.chainWorld !== undefined && pin.chainWorld.length > 0) {
          const points = litVertices(view.region, pin.chainWorld, toBoneLocal);
          if (points.length > 0) {
            const cloudGeometry = new BufferGeometry();
            // CROSSES, NOT POINTS — `PointsMaterial` does not rasterise under
            // WebGPU (the cloud existed, the probe counted it, nothing drew).
            // Line rendering is proven here, so each lit vertex is three
            // crossed segments.
            cloudGeometry.setAttribute('position', new Float32BufferAttribute(crosses(points, r), 3));
            const cloud = new LineSegments(
              cloudGeometry,
              new LineBasicMaterial({ color: LIT, depthTest: false, transparent: true, opacity: 0.95 }),
            );
            cloud.name = `hand-vertices:${handBoneName}:${pin.id}`;
            cloud.renderOrder = 1000;
            cloud.frustumCulled = false;
            markViewportDebris(cloud, 'hand-marker'); // same door, same stamp as the discs
            bone.add(cloud);
            layer.clouds.push(cloud);
          }
        }

        // ── THE KNUCKLE (ADR-024 §2.2): the second row, on the model ────────
        // Smaller than the tip and wearing an open ring — the same hue (the
        // identity), one step quieter (it is a row position, not a fingertip),
        // and pickable in its own right so it can be re-aimed or removed.
        if (pin.knuckleWorld !== undefined && pin.knuckleWorld !== null) {
          const knuckleId = `${pin.id}:knuckle`;
          const kLit = view.hovered === knuckleId;
          const kDot = new Mesh(new SphereGeometry(r * (kLit ? 1.9 : 1.2), 14, 10), dotMaterial(color, 0.95));
          kDot.name = `hand-knuckle:${handBoneName}:${pin.id}`;
          kDot.userData['handBone'] = handBoneName;
          kDot.userData['markerId'] = knuckleId;
          place(kDot, pin.knuckleWorld);
          layer.dots.set(knuckleId, kDot);
          const kRing = new Mesh(new TorusGeometry(r * 2.0, r * 0.28, 8, 24), dotMaterial(color, 0.8));
          kRing.name = `hand-knuckle-ring:${handBoneName}:${pin.id}`;
          place(kRing, pin.knuckleWorld);
          if (kLit) {
            const hover = new Mesh(new TorusGeometry(r * 2.9, r * 0.22, 8, 24), dotMaterial(0xffffff, 0.85));
            hover.name = `hand-hover:${handBoneName}:${knuckleId}`;
            place(hover, pin.knuckleWorld);
          }
        }
      }
    },
  };
}

/**
 * Region vertices lying within about one voxel of a branch's medial path,
 * returned in BONE-LOCAL space ready to draw.
 *
 * One voxel is the honest radius: the branch chain IS a sequence of voxel
 * centres, so anything the analyzer counted toward this branch is within roughly
 * that of the path. A wider radius would light neighbours the measurement never
 * attributed here, which would make the picture flattering rather than true.
 */
function litVertices(region: MeasuredRegion, chainWorld: readonly P3[], toBoneLocal: Matrix4): number[] {
  const radius = region.voxelSize * 1.1;
  const radiusSq = radius * radius;
  const out: number[] = [];
  const v = new Vector3();
  for (let i = 0; i < region.positions.length; i += 3) {
    const x = region.positions[i]!;
    const y = region.positions[i + 1]!;
    const z = region.positions[i + 2]!;
    let near = false;
    for (const p of chainWorld) {
      const dx = x - p.x;
      const dy = y - p.y;
      const dz = z - p.z;
      if (dx * dx + dy * dy + dz * dz <= radiusSq) {
        near = true;
        break;
      }
    }
    if (!near) continue;
    v.set(x, y, z).applyMatrix4(toBoneLocal);
    out.push(v.x, v.y, v.z);
  }
  return out;
}

/**
 * Three short axis-aligned segments through each point — a cross small enough to
 * read as a lit vertex rather than as a shape of its own. A voxel and a quarter:
 * the first pass used an eighth of a voxel, reasoning that a dense finger would
 * fill in as a solid block — but a hand's voxel is a couple of millimetres, so
 * an eighth of one is invisible to anybody not already looking for it.
 * Legibility beats the tidier picture: a mark nobody can see is not a
 * conservative choice, it is an absent feature.
 */
function crosses(points: readonly number[], voxelSize: number): number[] {
  const r = voxelSize * 1.25;
  const out: number[] = [];
  for (let i = 0; i < points.length; i += 3) {
    const x = points[i]!;
    const y = points[i + 1]!;
    const z = points[i + 2]!;
    out.push(x - r, y, z, x + r, y, z);
    out.push(x, y - r, z, x, y + r, z);
    out.push(x, y, z - r, x, y, z + r);
  }
  return out;
}
